import { ReactNode } from "react";

/**
 * 一組動作裡的一項（例：「批次動作」底下的「批次編輯」）
 */
export interface PageActionItem {
  /**
   * 識別鍵（同一頁不重複）
   */
  key: string;
  /**
   * 動作名稱
   */
  label: string;
  /**
   * 圖示（手機的清單會用到）
   */
  icon?: ReactNode;
  /**
   * 點了之後要做的事
   */
  onClick: () => void;
  /**
   * 能不能用（例：勾選超過上限）；不能用時顯示成停用，並用 `disabledReason` 說明原因
   */
  disabled?: boolean;
  /**
   * 不能用的原因
   */
  disabledReason?: string;
  /**
   * 是不是危險的動作（例：刪除）；手機上危險的動作不直接放在選取列上，一律收進「更多」的最下面
   */
  isDestructive?: boolean;
}

/**
 * 頁面上方的一個動作
 */
export interface PageAction extends Omit<PageActionItem, "onClick"> {
  /**
   * 動作的種類：
   * - `primary`：這一頁的主要動作（例：新增），一頁最多一個
   * - `page`：不用勾選就能做的動作（例：同步）
   * - `selection`：要先勾選資料才能做的動作（例：匯出、批次動作）
   */
  kind: "primary" | "page" | "selection";
  /**
   * 點了之後要做的事；有 `items` 的動作不用給
   */
  onClick?: () => void;
  /**
   * 這個動作底下的項目（例：「批次動作」底下的批次編輯、批次刪除）。
   * 有給的話，桌面是一顆下拉按鈕；手機攤平成一項一項，不做兩層選單
   */
  items?: PageActionItem[];
}

/**
 * 把動作依種類分開，各自維持頁面宣告的順序。
 * 主要動作只取第一個（一頁最多一個）
 */
export const groupPageActions = <T extends PageAction>(actions: T[]) => ({
  primaryAction: actions.find((action) => action.kind === "primary"),
  pageActions: actions.filter((action) => action.kind === "page"),
  selectionActions: actions.filter((action) => action.kind === "selection"),
});

/**
 * 手機標題列上「不用勾選的動作」怎麼放。
 *
 * 標題列上只放有文字的按鈕，不放只有圖示的按鈕（只有圖示看不出是什麼動作）：
 * - 有主要動作：標題列的位置留給主要動作，其餘全部收進「⋯」
 * - 沒有主要動作、而且只有一個：直接放在標題列，帶文字
 * - 沒有主要動作、有兩個以上：全部收進「⋯」
 *
 * @param pageActions 不用勾選的動作
 * @param hasPrimaryAction 這一頁有沒有主要動作
 */
export const resolveMobilePageActions = <T extends PageAction>(
  pageActions: T[],
  hasPrimaryAction: boolean
): { inlineAction: T | undefined; overflowActions: T[] } =>
  !hasPrimaryAction && pageActions.length === 1
    ? { inlineAction: pageActions[0], overflowActions: [] }
    : { inlineAction: undefined, overflowActions: pageActions };

/**
 * 手機用：把有子項目的動作攤平成一項一項（放在原本那個動作的位置），沒有子項目的原樣保留
 */
export const flattenPageActions = (actions: PageAction[]): PageAction[] =>
  actions.flatMap((action) =>
    action.items == null
      ? [action]
      : action.items.map<PageAction>((item) => ({ ...item, kind: action.kind }))
  );

/**
 * 把「要勾選的動作」分成可以直接放在列上的，與一定收進「更多」的（危險的動作）
 */
export const splitInlineCandidates = <T extends PageAction>(selectionActions: T[]) => ({
  inlineCandidates: selectionActions.filter((action) => action.isDestructive !== true),
  forcedOverflowActions: selectionActions.filter((action) => action.isDestructive === true),
});

/**
 * 照順序算出列上放得下幾個動作。
 *
 * 全部放得下、而且沒有一定要收進「更多」的動作時，不需要「更多」；
 * 否則要先留「更多」的寬度，再看前面放得下幾個
 *
 * @param itemWidths 每個動作按鈕的寬度（照順序）
 * @param gap 按鈕之間的間距
 * @param availableWidth 這一列可以用的寬度
 * @param moreWidth 「更多」按鈕的寬度
 * @param hasForcedOverflow 有沒有一定要收進「更多」的動作
 */
export const countFittingItems = ({
  itemWidths,
  gap,
  availableWidth,
  moreWidth,
  hasForcedOverflow,
}: {
  itemWidths: number[];
  gap: number;
  availableWidth: number;
  moreWidth: number;
  hasForcedOverflow: boolean;
}): number => {
  const totalWidth =
    itemWidths.reduce((sum, width) => sum + width, 0) + gap * Math.max(itemWidths.length - 1, 0);

  if (!hasForcedOverflow && totalWidth <= availableWidth) {
    return itemWidths.length;
  }

  // 要有「更多」：先扣掉它的寬度，再照順序放
  let usedWidth = moreWidth;
  let count = 0;

  for (const width of itemWidths) {
    const nextUsedWidth = usedWidth + gap + width;

    if (nextUsedWidth > availableWidth) {
      break;
    }

    usedWidth = nextUsedWidth;
    count += 1;
  }

  return count;
};
